import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Eye, EyeOff, Pencil, Plus, Shuffle } from 'lucide-react';
import { useState } from 'react';

import {
  contentFieldsHide,
  contentFieldsImpact,
  contentFieldsReorder,
  contentFieldsRestore,
} from '@/shared/api/generated/content/content';
import type { FieldBody, TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import { RECORDS_IN } from './kinds';
import styles from './SchemaPage.module.scss';

export interface FieldTreeProps {
  type: TypeBody;
  fields: FieldBody[];
  /** Добавить поле: в тип или в группу. */
  onAdd: (parent: FieldBody | undefined) => void;
  onEdit: (field: FieldBody) => void;
  onRetype: (field: FieldBody) => void;
  onChanged: () => void;
  /** Схема прода — только смотреть: меняется она переносом с дева. */
  readOnly?: boolean;
}

/**
 * Поля типа деревом: поля повторяющейся группы — внутри неё, со своим
 * порядком и своей кнопкой «добавить». У каждого поля — метки настроек,
 * чтобы устройство формы читалось без открытия каждого поля.
 */
export function FieldTree({
  type,
  fields,
  onAdd,
  onEdit,
  onRetype,
  onChanged,
  readOnly = false,
}: FieldTreeProps) {
  const [hiding, setHiding] = useState<FieldBody>();

  const visible = fields.filter((item) => !item.hidden);
  const top = visible.filter((item) => item.parent_id === undefined);
  const hidden = fields.filter((item) => item.hidden);

  const act = (run: Promise<unknown>, done: string) => {
    run
      .then(() => {
        toast.success(done);
        onChanged();
      })
      .catch((cause: unknown) => {
        toast.error('Не удалось выполнить действие', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      });
  };

  // Порядок задаётся целиком, а не «сдвинуть одно»: иначе два человека,
  // переставляющие поля одновременно, получают перемешанный список.
  const move = (list: FieldBody[], index: number, delta: number) => {
    const next = [...list];
    const moved = next[index];
    const neighbour = next[index + delta];

    if (moved === undefined || neighbour === undefined) {
      return;
    }

    next[index] = neighbour;
    next[index + delta] = moved;

    act(
      contentFieldsReorder(
        type.id,
        { fields: next.map((item) => item.id) },
        { 'If-Match': type.etag },
      ),
      'Порядок изменён',
    );
  };

  const row = (item: FieldBody, list: FieldBody[], index: number, nested: boolean) => (
    <div className={`${styles['field'] ?? ''} ${nested ? (styles['field--nested'] ?? '') : ''}`}>
      {!readOnly && (
        <div className={styles['field__order']}>
          <button
            type="button"
            aria-label={`Поднять поле «${item.name}»`}
            disabled={index === 0}
            onClick={() => {
              move(list, index, -1);
            }}
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            aria-label={`Опустить поле «${item.name}»`}
            disabled={index === list.length - 1}
            onClick={() => {
              move(list, index, 1);
            }}
          >
            <ChevronDown size={14} />
          </button>
        </div>
      )}

      <div className={styles['field__main']}>
        <span className={styles['field__name']}>
          {item.name}
          {item.required && (
            <span className={styles['field__required']} title="Обязательное">
              *
            </span>
          )}
        </span>
        <span className={styles['field__key']}>{item.key}</span>
        <FieldMarks field={item} fields={visible} />
      </div>

      <span className={styles['field__type']}>{item.type_title}</span>

      {!readOnly && (
        <div className={styles['field__actions']}>
          {!nested && (
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label={`Сменить вид поля «${item.name}»`}
              onClick={() => {
                onRetype(item);
              }}
            >
              <Shuffle size={15} />
            </Button>
          )}
          <Button
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label={`Изменить поле «${item.name}»`}
            onClick={() => {
              onEdit(item);
            }}
          >
            <Pencil size={15} />
          </Button>
          <Button
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label={`Скрыть поле «${item.name}»`}
            onClick={() => {
              setHiding(item);
            }}
          >
            <EyeOff size={15} />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles['fields']}>
      {top.length === 0 ? (
        <p className={styles['muted']}>
          Полей пока нет. Первым обычно заводят заголовок — по нему запись видно в списках.
        </p>
      ) : (
        <ul className={styles['field-list']}>
          {top.map((item, index) => {
            const children = visible.filter((child) => child.parent_id === item.id);

            return (
              <li key={item.id}>
                {row(item, top, index, false)}

                {item.type === 'group' && (
                  <div className={styles['group']}>
                    {children.length === 0 ? (
                      <p className={styles['muted']}>
                        В группе пока нет полей. Например, у вопросов FAQ — «Вопрос» и «Ответ».
                      </p>
                    ) : (
                      <ul className={styles['field-list']}>
                        {children.map((child, childIndex) => (
                          <li key={child.id}>{row(child, children, childIndex, true)}</li>
                        ))}
                      </ul>
                    )}
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="ghost"
                        tone="neutral"
                        iconLeft={<Plus size={14} />}
                        onClick={() => {
                          onAdd(item);
                        }}
                      >
                        Поле в группу «{item.name}»
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <div>
          <Button
            variant="outline"
            tone="neutral"
            iconLeft={<Plus size={15} />}
            onClick={() => {
              onAdd(undefined);
            }}
          >
            Добавить поле
          </Button>
        </div>
      )}

      {hidden.length > 0 && (
        <div className={styles['hidden-block']}>
          <h3 className={styles['hidden-block__title']}>Скрытые поля</h3>
          <p className={styles['muted']}>
            В форме их нет, значения в записях остались. Вернуть можно одним нажатием.
          </p>

          <ul className={styles['field-list']}>
            {hidden.map((item) => (
              <li key={item.id} className={styles['field']}>
                <div className={styles['field__main']}>
                  <span className={styles['field__name']}>{item.name}</span>
                  <span className={styles['field__key']}>{item.key}</span>
                </div>

                <span className={styles['field__type']}>{item.type_title}</span>

                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    iconLeft={<Eye size={15} />}
                    onClick={() => {
                      act(contentFieldsRestore(item.id), 'Поле возвращено');
                    }}
                  >
                    Вернуть
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <HideFieldDialog
        field={hiding}
        onClose={() => {
          setHiding(undefined);
        }}
        onHidden={onChanged}
      />
    </div>
  );
}

/** Метки настроек поля: что в нём особенного, без открытия диалога. */
function FieldMarks({ field, fields }: { field: FieldBody; fields: FieldBody[] }) {
  const marks: string[] = [];
  const rules = field.rules;

  if (rules.unique === true) marks.push('уникальное');

  if (rules.max_length !== undefined) marks.push(`до ${String(rules.max_length)} симв.`);

  if (rules.min !== undefined || rules.max !== undefined) {
    marks.push(
      `${rules.min === undefined ? '…' : String(rules.min)}–${rules.max === undefined ? '…' : String(rules.max)}`,
    );
  }

  if (rules.pattern !== undefined && rules.pattern !== '') marks.push('формат');

  if (rules.max_items !== undefined) marks.push(`не больше ${String(rules.max_items)}`);

  if (field.show_if !== undefined) {
    const target = fields.find((item) => item.key === field.show_if?.field);

    marks.push(`если «${target?.name ?? field.show_if.field}»`);
  }

  if (field.translatable) marks.push('переводимое');

  if (field.width === 'half') marks.push('½ строки');

  if (field.tab !== '') marks.push(`вкладка «${field.tab}»`);

  if (marks.length === 0) {
    return null;
  }

  return (
    <span className={styles['field__marks']}>
      {marks.map((mark) => (
        <span key={mark} className={styles['mark']}>
          {mark}
        </span>
      ))}
    </span>
  );
}

/** Скрытие поля — с числом записей, где оно заполнено. */
function HideFieldDialog({
  field,
  onClose,
  onHidden,
}: {
  field: FieldBody | undefined;
  onClose: () => void;
  onHidden: () => void;
}) {
  const impact = useQuery({
    queryKey: ['field-impact', field?.id, 'hide'],
    enabled: field !== undefined,
    queryFn: async () => {
      const response = await contentFieldsImpact(field?.id ?? '', {});

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на подсчёт');
      }

      return response.data;
    },
  });

  const description = (() => {
    if (impact.data === undefined) {
      return 'Считаем, где поле заполнено…';
    }

    const group = field?.type === 'group' ? ' Поля группы скроются вместе с ней.' : '';

    if (impact.data.filled === 0) {
      return `Поле нигде не заполнено — скрытие ничего не заденет.${group}`;
    }

    return `Поле заполнено в ${pluralize(impact.data.filled, RECORDS_IN)}. Значения останутся в записях, но в форме поля не будет. Вернуть можно в «Скрытых полях».${group}`;
  })();

  return (
    <ConfirmDialog
      open={field !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={`Скрыть поле «${field?.name ?? ''}»?`}
      description={description}
      confirmLabel="Скрыть"
      onConfirm={async () => {
        if (field === undefined) {
          return;
        }

        try {
          await contentFieldsHide(field.id);
          toast.success('Поле скрыто');
          onHidden();
        } catch (cause) {
          toast.error('Не удалось скрыть', {
            description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
          });

          throw cause;
        }
      }}
    />
  );
}
